/**
 * Girasol Toolbox companion service.
 *
 * The companion app (GirasolToolbox.exe) runs locally on the user's Windows PC
 * at http://127.0.0.1:51820.  It exposes a pairing flow and a preset manifest.
 */

import JSZip from 'jszip'

const COMPANION_BASE = 'http://127.0.0.1:51820'
const TOKEN_KEY = 'girasol_toolbox_token'
const SYNC_KEY = 'girasol_toolbox_last_sync'

// ── Bundle constants (must match toolbox/companion/bundle.py) ─────────────────

const MANIFEST_NAME = 'girasol_bundle.json'
const BUNDLE_SCHEMA_VERSION = 1

/** Maps backend slicer names → Toolbox Slicer IDs (slicer_paths.py Slicer literal). */
const SLICER_TO_TOOLBOX: Record<string, string> = {
  'creality print': 'creality_print',
  'orcaslicer': 'orca',
  'bambu_studio': 'bambu',
  'creality_slicer': 'creality_slicer',
}

/** Returns the backend URL for the Toolbox installer download (presigned R2 redirect).
 *  Default format is 'zip' — avoids browser security warnings vs. raw .exe downloads. */
export function getInstallerDownloadUrl(apiBase: string, format: 'zip' | 'exe' = 'zip'): string {
  return `${apiBase}/api/toolbox/download?format=${format}`
}

export type PresetPrinter = 'k1' | 'k1max' | 'h2d'
export type PresetSlicer = 'creality_print' | 'orcaslicer' | 'bambu_studio'
export type PresetSection = 'machine' | 'filament' | 'process'

/** Returns the backend URL to download a preset bundle zip for a specific printer/slicer/section. */
export function getPresetBundleUrl(
  apiBase: string,
  printer: PresetPrinter,
  slicer: PresetSlicer,
  section: PresetSection,
): string {
  return `${apiBase}/api/toolbox/preset-bundle?printer=${printer}&slicer=${slicer}&section=${section}`
}

// ── Farm custom presets (per-farm DB-stored presets) ──────────────────────────

export interface FarmCustomPreset {
  id: string
  name: string
  slicer: string
  printer: string
  section: string
  nozzle?: string
  json_content: Record<string, unknown>
  created_at: string
}

/** Fetch farm-specific custom presets from the backend. Requires a farm JWT. */
export async function fetchFarmCustomPresets(apiBase: string, authToken: string): Promise<FarmCustomPreset[]> {
  const url = `${apiBase}/api/farms/me/custom-presets`
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 10000)

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`)
    }

    return response.json()
  } catch (error) {
    clearTimeout(timeoutId)
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timeout')
    }
    throw error
  }
}

// ── Doctor custom presets (per-doctor DB-stored presets) ──────────────────────────

export interface DoctorCustomPreset {
  id: string
  name: string
  slicer: string
  printer: string
  section: string
  nozzle?: string
  json_content: Record<string, unknown>
  created_at: string
}

/** Fetch doctor-specific custom presets from the backend. Requires a doctor JWT. */
export async function fetchDoctorCustomPresets(apiBase: string, authToken: string): Promise<DoctorCustomPreset[]> {
  const url = `${apiBase}/api/doctors/me/custom-presets`
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 10000)

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`)
    }

    return response.json()
  } catch (error) {
    clearTimeout(timeoutId)
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timeout')
    }
    throw error
  }
}

export interface ToolboxPreset {
  id: string
  name: string
  version: string
  slicer: string
  kind: string
  installed: boolean
}

export interface PresetsResponse {
  presets: ToolboxPreset[]
}

export interface SlicerInfo {
  slicer: string
  detected: boolean
  user_dir: string | null
}

export interface SlicersResponse {
  slicers: SlicerInfo[]
}

export interface PairStartResponse {
  pair_id: string
  code_hint?: string
}

export interface PairConfirmResponse {
  access_token: string
}

// ── token helpers ─────────────────────────────────────────────────────────────

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

function storeToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY)
}

// ── sync timestamp ────────────────────────────────────────────────────────────

export function recordSync() {
  localStorage.setItem(SYNC_KEY, new Date().toISOString())
}

export function getLastSync(): Date | null {
  const v = localStorage.getItem(SYNC_KEY)
  return v ? new Date(v) : null
}

// ── API calls ─────────────────────────────────────────────────────────────────

/** Returns true if the companion app is reachable. */
export async function isCompanionAvailable(): Promise<boolean> {
  try {
    const res = await fetch(`${COMPANION_BASE}/health`, { signal: AbortSignal.timeout(2000) })
    return res.ok
  } catch {
    return false
  }
}

/** Step 1 of pairing — companion shows a 6-digit code as a tray balloon. */
export async function startPair(): Promise<PairStartResponse> {
  const res = await fetch(`${COMPANION_BASE}/pair`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ origin: window.location.origin }),
  })
  if (!res.ok) throw new Error(`Pair start failed: ${res.status}`)
  return res.json() as Promise<PairStartResponse>
}

/** Step 2 of pairing — user enters the 6-digit code shown on the tray icon. */
export async function confirmPair(pairId: string, code: string): Promise<string> {
  const res = await fetch(`${COMPANION_BASE}/pair/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pair_id: pairId, code }),
  })
  if (!res.ok) throw new Error(`Pair confirmation failed: ${res.status}`)
  const data = (await res.json()) as PairConfirmResponse
  storeToken(data.access_token)
  recordSync()
  return data.access_token
}

/** Fetch installed presets.  Stores sync timestamp on success. */
export async function getPresets(token: string): Promise<PresetsResponse> {
  const res = await fetch(`${COMPANION_BASE}/presets`, {
    headers: { Authorization: `Bearer ${token}` },
    signal: AbortSignal.timeout(5000),
  })
  if (res.status === 401) {
    clearToken()
    throw new Error('Toolbox token expired — please pair again')
  }
  if (!res.ok) throw new Error(`Preset fetch failed: ${res.status}`)
  const data = (await res.json()) as PresetsResponse
  recordSync()
  return data
}

/** Fetch which slicers are detected on the local machine. */
export async function getSlicers(token: string): Promise<SlicersResponse> {
  const res = await fetch(`${COMPANION_BASE}/slicers`, {
    headers: { Authorization: `Bearer ${token}` },
    signal: AbortSignal.timeout(5000),
  })
  if (res.status === 401) {
    clearToken()
    throw new Error('Toolbox token expired — please pair again')
  }
  if (!res.ok) throw new Error(`Slicers fetch failed: ${res.status}`)
  return res.json() as Promise<SlicersResponse>
}

/** Install a preset file into a slicer via the companion. */
export async function installPresetFile(
  token: string,
  slicer: string,
  kind: string,
  name: string,
  content: string,
  overwrite = false,
): Promise<{ installed_path: string }> {
  const res = await fetch(`${COMPANION_BASE}/install`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ slicer, kind, name, content, overwrite }),
  })
  if (res.status === 401) {
    clearToken()
    throw new Error('Toolbox token expired — please pair again')
  }
  if (res.status === 409) throw new Error('Preset already exists. Enable overwrite to replace it.')
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error((err as { detail?: string }).detail ?? `Install failed: ${res.status}`)
  }
  return res.json() as Promise<{ installed_path: string }>
}

// ── Farm preset bundle builder (produces ZIP for Toolbox import) ─────────────

/** Slicers that require a .info sidecar alongside the .json preset file. */
const SLICERS_NEEDING_INFO = new Set(['orca', 'bambu'])

/** Generate a .info sidecar content for OrcaSlicer / Bambu Studio presets. */
function generateInfoContent(): string {
  const settingId = crypto.randomUUID().replace(/-/g, '').slice(0, 16)
  const updatedTime = Math.floor(Date.now() / 1000).toString()
  return [
    'sync_info =',
    'user_id =',
    `setting_id = ${settingId}`,
    'base_id =',
    `updated_time = ${updatedTime}`,
    '',
  ].join('\n')
}

export interface BundleItemSpec {
  slicer: string   // Toolbox Slicer ID (e.g. "creality_print")
  kind: string     // PresetKind (machine | filament | process)
  name: string     // base name without extension
  filename: string // file name inside the archive (e.g. "K1 Max 0.8 nozzle.json")
  content: string  // JSON preset content as string
}

/** Build a Girasoles bundle ZIP blob from one or more farm presets.
 *
 *  The ZIP contains ``girasol_bundle.json`` (the manifest) and the preset
 *  files under ``presets/<slicer>/<kind>/<filename>``, matching the
 *  Toolbox's bundle.py schema.
 *
 *  For OrcaSlicer and Bambu Studio targets, a ``.info`` sidecar file is
 *  automatically included alongside the ``.json`` preset — the slicer
 *  silently ignores presets without it.
 */
export async function buildFarmPresetBundle(
  items: BundleItemSpec[],
  description?: string,
): Promise<Blob> {
  const zip = new JSZip()

  const manifest = {
    schema_version: BUNDLE_SCHEMA_VERSION,
    kind: 'presets',
    created_at: new Date().toISOString(),
    created_by: 'girasol-web',
    description: description ?? 'Farm custom presets exported from Girasoles',
    items: items.map(it => ({
      slicer: it.slicer,
      kind: it.kind,
      name: it.name,
      path: `presets/${it.slicer}/${it.kind}/${it.filename}`,
      filename: it.filename,
    })),
  }

  zip.file(MANIFEST_NAME, JSON.stringify(manifest, null, 2))

  for (const it of items) {
    const archivePath = `presets/${it.slicer}/${it.kind}/${it.filename}`
    zip.file(archivePath, it.content)

    // Include .info sidecar for OrcaSlicer / Bambu Studio
    if (SLICERS_NEEDING_INFO.has(it.slicer)) {
      const infoFilename = it.filename.replace(/\.json$/i, '.info')
      const infoPath = `presets/${it.slicer}/${it.kind}/${infoFilename}`
      zip.file(infoPath, generateInfoContent())
    }
  }

  return zip.generateAsync({ type: 'blob' })
}

/** Convert a single FarmCustomPreset to a BundleItemSpec. */
export function presetToBundleItem(preset: FarmCustomPreset): BundleItemSpec {
  const slicer = SLICER_TO_TOOLBOX[preset.slicer] ?? preset.slicer
  const filename = `${preset.name}.json`
  return {
    slicer,
    kind: preset.section,
    name: preset.name,
    filename,
    content: JSON.stringify(preset.json_content, null, 2),
  }
}
